import type {
  EventListParams,
  EventListResponse,
  EventDeliveryAttemptResponse,
  EventDetailResponse,
  EventManualTriggerRequest,
  EventManualTriggerResponse,
} from '../types';
import type { RequestFn } from '../client';

export class Events {
  constructor(private readonly request: RequestFn) {}

  async list(merchantId: string, params?: EventListParams): Promise<EventListResponse> {
    return this.request('POST', `/events/${encodeURIComponent(merchantId)}`, { body: params });
  }

  async listDeliveryAttempts(
    merchantId: string,
    eventId: string,
  ): Promise<EventDeliveryAttemptResponse[]> {
    return this.request(
      'GET',
      `/events/${encodeURIComponent(merchantId)}/${encodeURIComponent(eventId)}/attempts`,
    );
  }

  async retryDelivery(merchantId: string, eventId: string): Promise<EventDetailResponse> {
    return this.request(
      'POST',
      `/events/${encodeURIComponent(merchantId)}/${encodeURIComponent(eventId)}/retry`,
    );
  }

  /**
   * Fire an outgoing webhook event by hand for one object.
   * `POST /events/{merchant_id}/trigger`
   *
   * Not a replay — see {@link EventManualTriggerRequest}. Needs the
   * `WebhookEventTrigger` permission, which is separate from the one that
   * authorizes {@link retryDelivery}, and is JWT-only: a forged payload must
   * be attributable to a person.
   */
  async trigger(
    merchantId: string,
    body: EventManualTriggerRequest,
  ): Promise<EventManualTriggerResponse> {
    return this.request('POST', `/events/${encodeURIComponent(merchantId)}/trigger`, { body });
  }

  // --- Profile-scoped listing (Task 4.12) ---

  /**
   * List events (profile-scoped). `POST /events/profile/list`
   *
   * Takes the same constraints as {@link list} — it is the same endpoint
   * narrowed to the caller's own shop, so `profile_id` is redundant and
   * ignored there.
   */
  async listByProfile(params?: EventListParams): Promise<EventListResponse> {
    return this.request('POST', '/events/profile/list', { body: params });
  }

  /**
   * Fire an outgoing webhook event by hand (profile-scoped).
   * `POST /events/profile/trigger`
   *
   * The shop-user twin of {@link trigger}; the shop is taken from the auth
   * context, so `profile_id` on the body is ignored.
   */
  async triggerByProfile(body: EventManualTriggerRequest): Promise<EventManualTriggerResponse> {
    return this.request('POST', '/events/profile/trigger', { body });
  }
}
